import { DragDropProvider, type DragEndEvent } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import {
  AccessTimeOutlined as ClockIcon,
  CheckroomOutlined as FittingRoomIcon,
  DragIndicatorOutlined as DragHandleIcon,
  ExpandMore as ExpandMoreIcon,
  Logout as LogOutIcon,
  NotificationsOutlined as RequestsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState, ErrorState } from "@/components/common";
import {
  ComparisonPageHeaderShell,
  InsightsHeaderSelectorField,
  InsightsHeaderSelectorRow,
} from "@/components/common/ReportingPageHeaderShell";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import ItemCard from "@/pages/store-insights/live-experience/live-view/components/ItemCard";
import RequestCard from "@/pages/store-insights/live-experience/live-view/components/RequestCard";
import { STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY } from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsSingleStoreSelection } from "@/pages/store-insights/shared/useStoreInsightsSingleStoreSelection";
import { LiveViewService } from "@/services";
import { DebugService } from "@/services/features/DebugService";
import { PusherService } from "@/services/features/PusherService";
import { Store } from "@/services/features/StoreService";
import { formatInteger } from "@/utils/number";
import { UserRole } from "@/utils/roleManager";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatDurationClock } from "@/utils/time";

import { Area, Room } from "./types";

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  index: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`area-tabpanel-${index}`}
      aria-labelledby={`area-tab-${index}`}
      className="w-full"
      {...other}
    >
      {value === index && <Box className="w-full">{children}</Box>}
    </div>
  );
}

interface LiveViewState {
  loading: {
    isLoadingInitial: boolean;
    isLoadingStores: boolean;
    isLoadingData: boolean;
  };
  error: string | null;
  data: {
    areas: Area[];
  };
  selection: {
    activeTab: string;
    selectedStore: Store | null;
  };
}

const AREA_TAB_HEIGHT = 48;
const COMPARE_MODE_CONTROL_HEIGHT = 38;
const ROOM_CARD_HEADER_MIN_HEIGHT = 88;
const ROOM_SKELETON_COUNT = 4;

interface LiveViewSkeletonProps {
  canUseCompareMode: boolean;
  isMobile: boolean;
}

const LiveViewSkeleton: React.FC<LiveViewSkeletonProps> = ({ canUseCompareMode, isMobile }) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 1, sm: 2 },
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Tabs value={false} aria-hidden sx={{ minWidth: 0, flex: 1 }}>
        {[112, 144, 96].map((width, index) => (
          <Tab
            key={width}
            disabled
            label={
              <Box sx={{ textAlign: "start" }} className="pt-2 pb-2">
                <Skeleton
                  variant="text"
                  width={width}
                  sx={{
                    height: { xs: 20, md: 25 },
                    transform: "none",
                  }}
                />
              </Box>
            }
            value={index}
            sx={{
              minHeight: AREA_TAB_HEIGHT,
              "&.Mui-disabled": { opacity: 1 },
            }}
          />
        ))}
      </Tabs>
    </Box>

    {canUseCompareMode && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: { xs: 2, sm: 4 },
          pt: isMobile ? 2 : 4,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: COMPARE_MODE_CONTROL_HEIGHT,
            whiteSpace: "nowrap",
          }}
        >
          <Skeleton variant="rounded" width={38} height={20} sx={{ borderRadius: 10 }} />
          <Skeleton variant="circular" width={18} height={18} sx={{ mx: 0.5 }} />
          <Skeleton variant="text" width={108} sx={{ fontSize: "0.875rem" }} />
        </Box>
      </Box>
    )}

    <Box
      sx={{
        px: isMobile ? 2 : 4,
        pt: canUseCompareMode ? 0 : { xs: 2, sm: 4 },
        pb: isMobile ? 2 : 4,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          alignItems: "start",
          gap: 2,
        }}
      >
        {Array.from({ length: ROOM_SKELETON_COUNT }, (_, index) => (
          <Paper
            key={index}
            elevation={1}
            sx={{
              overflow: "hidden",
              borderRadius: 3,
              alignSelf: "start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                py: 2,
                minHeight: ROOM_CARD_HEADER_MIN_HEIGHT,
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="42%" sx={{ fontSize: "1.5rem" }} />
                <Skeleton variant="text" width="58%" sx={{ fontSize: "0.875rem" }} />
              </Box>
              <Skeleton
                variant="rounded"
                width={72}
                height={24}
                sx={{ display: { xs: "none", sm: "block" } }}
              />
              <Skeleton
                variant="rounded"
                width={64}
                height={24}
                sx={{ display: { xs: "none", sm: "block" } }}
              />
              <Skeleton variant="rounded" width={72} height={30} />
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  </>
);

const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = React.useRef(callback);

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    if (delay === null) return;

    const tick = () => savedCallback.current();
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

type RoomSubscriptionHandler = (rooms: Room[]) => void;

interface SortableRoomCardProps {
  areaId: string;
  children: (dragHandleRef: React.RefCallback<Element>) => React.ReactNode;
  index: number;
  room: Room;
}

const SortableRoomCard: React.FC<SortableRoomCardProps> = ({ areaId, children, index, room }) => {
  const { handleRef, isDragging, ref } = useSortable({
    id: room.id,
    index,
    group: areaId,
  });

  return (
    <Box
      ref={ref}
      sx={{
        minWidth: 0,
        opacity: isDragging ? 0.45 : 1,
        transition: "opacity 150ms ease",
      }}
    >
      {children(handleRef)}
    </Box>
  );
};

const LiveView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    filteredStores,
    selectedStore: persistedSelectedStore,
    loading: selectionLoading,
    onStoreChange,
  } = useStoreInsightsSingleStoreSelection({
    sessionKey: STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY,
  });

  // Service instances - move outside useEffect dependencies
  const liveViewService = React.useMemo(() => LiveViewService.getInstance(), []);
  const pusherService = React.useMemo(() => PusherService.getInstance(), []);
  const debugService = React.useMemo(() => DebugService.getInstance(), []);

  const getElapsedSeconds = React.useCallback((createdAt: string): number => {
    const diff = Date.now() - new Date(createdAt).getTime();
    return Math.max(0, Math.floor(diff / 1000));
  }, []);

  const formatTimer = React.useCallback(
    (createdAt: string): string => {
      return formatDurationClock(
        getElapsedSeconds(createdAt),
        i18n.resolvedLanguage || i18n.language,
      );
    },
    [getElapsedSeconds, i18n.resolvedLanguage, i18n.language],
  );

  // State management
  const [state, setState] = React.useState<LiveViewState>({
    loading: {
      isLoadingInitial: true,
      isLoadingStores: false,
      isLoadingData: false,
    },
    error: null,
    data: {
      areas: [],
    },
    selection: {
      activeTab: "",
      selectedStore: null as Store | null,
    },
  });
  const hasPendingStoreSelection =
    !selectionLoading && persistedSelectedStore?.id !== state.selection.selectedStore?.id;
  const isPageLoading =
    selectionLoading ||
    hasPendingStoreSelection ||
    state.loading.isLoadingInitial ||
    state.loading.isLoadingStores ||
    state.loading.isLoadingData;
  const canUseCompareMode = user?.role === UserRole.CRAVE_ADMIN;

  const lastGroupUpdateRef = React.useRef<{ [groupId: string]: number }>({});
  const pusherConnectionStateRef = React.useRef<string>("disconnected");
  const isFetchingRoomsRef = React.useRef(false);
  const roomSubscriptionsRef = React.useRef<Set<number>>(new Set());
  const roomToGroupMapRef = React.useRef<Map<number, string>>(new Map());
  const occupiedRoomIdsRef = React.useRef<Set<number>>(new Set());

  const fetchGroupState = React.useCallback(
    (groupId: string) => {
      return liveViewService
        .getState({
          groupIds: [parseInt(groupId)],
          includeStandaloneShoppers: true,
          includeOnHoldShoppers: false,
        })
        .then((stateResponse) => {
          if (!stateResponse.data) return;

          // Update last group update time
          lastGroupUpdateRef.current[groupId] = Date.now();

          // Process and update state for all rooms in the group
          setState((prevState) => {
            const updatedAreas = prevState.data.areas.map((area) => {
              if (area.id !== groupId) return area;

              return {
                ...area,
                rooms: area.rooms.map((room) => {
                  const stateRoom = stateResponse.data.rooms.find((r) => r.id === room.id);
                  if (!stateRoom) return room;

                  // Use helper methods to get shopper, items, and requests
                  const shopper = liveViewService.getShopperByRoom(stateResponse, room.id);
                  const items = liveViewService.getItemsByRoom(stateResponse, room.id);
                  const requests = liveViewService.getRequestsByRoom(stateResponse, room.id);

                  // Update room state with fresh data - explicitly handle vacant rooms
                  const updatedRoom = {
                    ...room,
                    status: shopper ? ("occupied" as const) : ("vacant" as const),
                    shopperDetails: shopper || undefined,
                    shopperName: shopper
                      ? shopper.name || t("operations.debug.labels.anonymous")
                      : undefined,
                    createdAt:
                      shopper && stateRoom.assignedAt
                        ? new Date(stateRoom.assignedAt).toISOString()
                        : undefined,
                    timer:
                      shopper && stateRoom.assignedAt
                        ? formatTimer(new Date(stateRoom.assignedAt).toISOString())
                        : undefined,
                    basketSize: shopper ? (items.length ?? 0) : undefined,
                    items: shopper ? items : [],
                    requests: shopper ? requests : [],
                  };

                  return updatedRoom;
                }),
              };
            });

            return {
              ...prevState,
              data: {
                ...prevState.data,
                areas: updatedAreas,
              },
            };
          });
        })
        .catch(() => {});
    },
    [formatTimer, liveViewService, t],
  );

  // Create a ref for the subscription function with initial value
  const roomSubscriptionHandlerRef = React.useRef<RoomSubscriptionHandler>(() => {});

  // Define the room subscription handler
  roomSubscriptionHandlerRef.current = React.useCallback(
    (rooms: Room[]) => {
      if (!user?.token) return;

      // Unsubscribe from rooms that are no longer in the list
      roomSubscriptionsRef.current.forEach((channelName) => {
        if (!rooms.some((room) => channelName === room.id)) {
          pusherService.unsubscribe(channelName);
          roomSubscriptionsRef.current.delete(channelName);
        }
      });

      // Subscribe to new rooms
      rooms.forEach((room) => {
        if (!roomSubscriptionsRef.current.has(room.id)) {
          // Get the group ID from the map instead of searching through state
          const groupId = roomToGroupMapRef.current.get(room.id);

          if (!groupId) {
            return;
          }

          pusherService.subscribe(room.id, () => {
            fetchGroupState(groupId).catch(() => {});
          });

          roomSubscriptionsRef.current.add(room.id);
        }
      });
    },
    [user?.token, pusherService, fetchGroupState],
  );

  const fetchChangingRoomData = React.useCallback(async () => {
    const currentStore = state.selection.selectedStore;
    if (!currentStore?.id || isFetchingRoomsRef.current) {
      return;
    }

    try {
      isFetchingRoomsRef.current = true;

      const changingRoomResponse = await liveViewService.getChangingRoomData(
        currentStore.id.toString(),
      );

      if (!changingRoomResponse.data) {
        setState((prevState) => ({
          ...prevState,
          loading: { ...prevState.loading, isLoadingData: false },
        }));
        return;
      }

      // Create a direct mapping from room ID to group ID
      const roomToGroupMap = new Map<number, string>();

      changingRoomResponse.data.forEach((area) => {
        const groupId = area.changingRoomGroup.id.toString();
        area.changingRooms.forEach((room) => {
          roomToGroupMap.set(room.id, groupId);
        });
      });

      // Store this mapping in a ref for use in setting up Pusher subscriptions
      roomToGroupMapRef.current = roomToGroupMap;

      // Transform changing room data into areas with complete initial state
      const areas = changingRoomResponse.data.map((area) => ({
        id: area.changingRoomGroup.id.toString(),
        name: area.changingRoomGroup.name,
        rooms: area.changingRooms.map((room) => ({
          id: room.id,
          number: room.name,
          status: "vacant" as const,
          shopperDetails: undefined,
          timer: undefined,
          basketSize: undefined,

          items: [],
          requests: [],
        })),
      }));

      // Set up initial areas structure
      setState((prevState) => ({
        ...prevState,
        error: null,
        data: {
          ...prevState.data,
          areas,
        },
        selection: {
          ...prevState.selection,
          activeTab: areas.length > 0 ? areas[0].id : prevState.selection.activeTab,
        },
      }));

      // Fetch complete state for each group

      // Process each area sequentially
      for (const currentArea of areas) {
        const stateResponse = await liveViewService.getState({
          groupIds: [parseInt(currentArea.id)],
          includeStandaloneShoppers: true,
          includeOnHoldShoppers: false,
        });

        if (!stateResponse.data) {
          throw new Error(t("liveView.errors.failedFetchChangingRoomData"));
        }

        // Update last group update time
        lastGroupUpdateRef.current[currentArea.id] = Date.now();

        // Process rooms for this area
        const processedRooms: Room[] = await Promise.all(
          currentArea.rooms.map(async (room): Promise<Room> => {
            const stateRoom = stateResponse.data.rooms.find((r) => r.id === room.id);
            if (!stateRoom) return room;

            // Use helper methods to get shopper, items, and requests
            const shopper = liveViewService.getShopperByRoom(stateResponse, room.id);
            const items = liveViewService.getItemsByRoom(stateResponse, room.id);
            const requests = liveViewService.getRequestsByRoom(stateResponse, room.id);

            return {
              ...room,
              status: shopper ? ("occupied" as const) : ("vacant" as const),
              shopperDetails: shopper || undefined,
              shopperName: shopper
                ? shopper.name || t("operations.debug.labels.anonymous")
                : undefined,
              items: shopper ? items : [],
              requests: shopper ? requests : [],

              createdAt:
                shopper && stateRoom.assignedAt
                  ? new Date(stateRoom.assignedAt).toISOString()
                  : undefined,
              timer:
                shopper && stateRoom.assignedAt
                  ? formatTimer(new Date(stateRoom.assignedAt).toISOString())
                  : undefined,
              basketSize: shopper ? (items.length ?? 0) : undefined,
            };
          }),
        );

        // After processing rooms, update the state synchronously
        const updatedArea: Area = {
          ...currentArea,
          rooms: processedRooms,
        };

        setState((prevState: LiveViewState): LiveViewState => ({
          ...prevState,
          data: {
            ...prevState.data,
            areas: prevState.data.areas.map((area) =>
              area.id === currentArea.id ? updatedArea : area,
            ),
          },
        }));
      }

      // Subscribe only after every initial group state has been applied. A Pusher
      // update received during initialization must not be overwritten by an
      // older initial getState response.
      const allRooms = areas.flatMap((area) => area.rooms);
      roomSubscriptionHandlerRef.current(allRooms);
    } catch {
      setState((prev) => ({
        ...prev,
        error: t("liveView.errors.failedFetchChangingRoomData"),
      }));
    } finally {
      isFetchingRoomsRef.current = false;
      setState((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, isLoadingData: false },
      }));
    }
  }, [formatTimer, liveViewService, state.selection.selectedStore, t]);

  // Initialize Pusher on component mount
  useEffect(() => {
    if (!user?.token) return;

    pusherService.initialize(user.token);

    // Log initial connection state and set up connection state handlers

    pusherConnectionStateRef.current = pusherService.getConnectionState();

    const handleConnectionStateChange = (states: { current: string; previous: string }) => {
      pusherConnectionStateRef.current = states.current;

      // Only re-subscribe if connection state changes to 'connected' from a different state
      if (states.current === "connected" && states.previous !== "connected") {
        setState((currentState: LiveViewState): LiveViewState => {
          const allRooms = currentState.data.areas.flatMap((area) => area.rooms);
          if (roomSubscriptionHandlerRef.current && allRooms.length > 0) {
            roomSubscriptionHandlerRef.current(allRooms);
          }
          return currentState;
        });
      }
    };

    // Subscribe to connection state changes using Pusher's native events
    const pusher = pusherService.getPusher();
    if (pusher) {
      pusher.connection.bind("state_change", handleConnectionStateChange);
    }

    return () => {
      if (pusher) {
        pusher.connection.unbind("state_change", handleConnectionStateChange);
      }
      pusherService.disconnect();
    };
  }, [pusherService, user?.token]);

  const [expandedRooms, setExpandedRooms] = useState<Set<number>>(new Set());
  const [vacatingShopperIds, setVacatingShopperIds] = useState<Set<number>>(new Set());
  const [isCompareMode, setIsCompareMode] = useState(false);

  useEffect(() => {
    const occupiedRoomIds = new Set<number>();

    state.data.areas.forEach((area) => {
      area.rooms.forEach((room) => {
        if (room.status === "occupied" && room.shopperDetails) {
          occupiedRoomIds.add(room.id);
        }
      });
    });

    setExpandedRooms((previous) => {
      const next = new Set(previous);
      occupiedRoomIds.forEach((roomId) => {
        if (!occupiedRoomIdsRef.current.has(roomId)) {
          next.add(roomId);
        }
      });
      return next;
    });

    occupiedRoomIdsRef.current = occupiedRoomIds;
  }, [state.data.areas]);

  const handleRoomToggle = (roomId: number) => {
    setExpandedRooms((previous) => {
      const next = new Set(previous);
      if (next.has(roomId)) {
        next.delete(roomId);
      } else {
        next.add(roomId);
      }
      return next;
    });
  };

  const handleRoomReorder = React.useCallback((areaId: string, event: DragEndEvent) => {
    const { source, target } = event.operation;

    if (
      !target ||
      !isSortable(source) ||
      !isSortable(target) ||
      source.group !== areaId ||
      target.group !== areaId ||
      source.index === target.index
    ) {
      return;
    }

    setState((previous) => ({
      ...previous,
      data: {
        ...previous.data,
        areas: previous.data.areas.map((area) => {
          if (area.id !== areaId) return area;

          const rooms = [...area.rooms];
          const [movedRoom] = rooms.splice(source.index, 1);
          if (!movedRoom) return area;

          rooms.splice(target.index, 0, movedRoom);
          return { ...area, rooms };
        }),
      },
    }));
  }, []);

  // Add state for timers
  const [timers, setTimers] = useState<{ [roomId: number]: string }>({});

  // Update timer every second for occupied rooms
  useInterval(() => {
    const updatedTimers: { [roomId: number]: string } = {};
    let hasUpdates = false;

    state.data.areas.forEach((area) => {
      area.rooms.forEach((room) => {
        if (room.status === "occupied" && room.createdAt) {
          const newTimer = formatTimer(room.createdAt);
          if (timers[room.id] !== newTimer) {
            hasUpdates = true;
          }
          updatedTimers[room.id] = newTimer;
        }
      });
    });

    if (hasUpdates) {
      setTimers(updatedTimers);
    }
  }, 1000); // Update every second

  useEffect(() => {
    if (!user || selectionLoading) {
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: {
        ...prev.loading,
        isLoadingInitial: false,
        isLoadingStores: false,
      },
    }));
  }, [selectionLoading, user]);

  // Effect to handle data fetching when store changes
  useEffect(() => {
    const selectedStoreId = state.selection.selectedStore?.id;
    if (!selectedStoreId || !user?.token) return;

    void fetchChangingRoomData();
    const roomSubscriptions = roomSubscriptionsRef.current;

    return () => {
      roomSubscriptions.forEach((roomId) => {
        pusherService.unsubscribe(roomId);
      });
      roomSubscriptions.clear();
    };
  }, [fetchChangingRoomData, pusherService, state.selection.selectedStore?.id, user?.token]);

  const handleStoreSelect = React.useCallback(
    (store: Store | null) => {
      if (store?.id === state.selection.selectedStore?.id) {
        return;
      }

      onStoreChange(store);

      roomSubscriptionsRef.current.forEach((roomId) => {
        pusherService.unsubscribe(roomId);
      });
      roomSubscriptionsRef.current.clear();

      setTimers({});
      setExpandedRooms(new Set());

      setState((prev) => ({
        ...prev,
        selection: {
          ...prev.selection,
          selectedStore: store,
          activeTab: "",
        },
        loading: {
          ...prev.loading,
          isLoadingData: !!store,
        },
        data: {
          ...prev.data,
          areas: [],
        },
      }));
    },
    [onStoreChange, pusherService, state.selection.selectedStore?.id],
  );

  useEffect(() => {
    if (selectionLoading) {
      return;
    }

    if (persistedSelectedStore?.id !== state.selection.selectedStore?.id) {
      handleStoreSelect(persistedSelectedStore ?? null);
    }
  }, [
    handleStoreSelect,
    persistedSelectedStore,
    selectionLoading,
    state.selection.selectedStore?.id,
  ]);

  const handleVacateRoom = async (shopperId: number) => {
    try {
      setVacatingShopperIds((previous) => new Set(previous).add(shopperId));

      const response = await debugService.vacateDebugShopper(shopperId);

      if (response.success) {
        enqueueSnackbar(t("liveView.notifications.roomVacatedSuccessfully"), {
          variant: "success",
        });
        // The room will be updated automatically via Pusher subscription
      } else {
        throw new Error(response.error || t("liveView.notifications.failedVacateRoom"));
      }
    } catch {
      enqueueSnackbar(t("liveView.notifications.failedVacateRoom"), { variant: "error" });
    } finally {
      setVacatingShopperIds((previous) => {
        const next = new Set(previous);
        next.delete(shopperId);
        return next;
      });
    }
  };

  // Add effect for fallback state fetching
  useEffect(() => {
    if (!state.selection.selectedStore?.id) return;

    // Check state every 5 seconds
    const stateCheckInterval = setInterval(() => {
      const now = Date.now();

      // Check Pusher connection state
      const isPusherDisconnected = pusherConnectionStateRef.current !== "connected";

      state.data.areas.forEach((area) => {
        const needsUpdate =
          isPusherDisconnected ||
          !lastGroupUpdateRef.current[area.id] ||
          now - lastGroupUpdateRef.current[area.id] > 30000;

        if (needsUpdate) {
          void fetchGroupState(area.id);
        }
      });
    }, 5000);

    return () => clearInterval(stateCheckInterval);
  }, [state.selection.selectedStore?.id, state.data.areas, fetchGroupState]);

  const renderRoomPaper = (room: Room, dragHandleRef?: React.RefCallback<Element>) => {
    const shopper = room.shopperDetails;
    const isOccupied = room.status === "occupied" && !!shopper;
    const isVacating = shopper ? vacatingShopperIds.has(shopper.id) : false;
    const isExpanded = expandedRooms.has(room.id);
    const isTimerOverdue = room.createdAt ? getElapsedSeconds(room.createdAt) > 600 : false;
    const requestCount = room.requests?.length ?? 0;
    const itemCount = room.items?.length ?? 0;
    const hasRequests = requestCount > 0;
    const hasItems = itemCount > 0;
    const hasRoomContent = hasRequests || hasItems;
    const isDetailsExpanded = isOccupied && isExpanded && hasRoomContent;
    const sortedItems = [...(room.items ?? [])].sort((first, second) =>
      first.sku.localeCompare(second.sku),
    );

    return (
      <Paper
        key={`room-${room.id}`}
        elevation={isOccupied ? 3 : 1}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 3,
          opacity: isOccupied ? 1 : 0.72,
          alignSelf: "start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 2.5 },
            py: 2,
            minHeight: ROOM_CARD_HEADER_MIN_HEIGHT,
            borderRadius: isDetailsExpanded ? "12px 12px 0 0" : 3,
            bgcolor: "common.white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {isCompareMode && (
              <Box
                ref={dragHandleRef}
                aria-label={room.number}
                sx={{
                  width: 24,
                  height: 24,
                  display: "grid",
                  placeItems: "center",
                  color: "text.secondary",
                  cursor: "grab",
                  touchAction: "none",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <DragHandleIcon fontSize="small" />
              </Box>
            )}
            {isOccupied && hasRoomContent ? (
              <IconButton
                size="small"
                aria-label={room.number}
                onClick={() => handleRoomToggle(room.id)}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: isExpanded ? "rotate(180deg)" : "none",
                    transition: theme.transitions.create("transform", { duration: 200 }),
                  }}
                />
              </IconButton>
            ) : (
              <Box
                aria-hidden
                sx={{
                  width: 40,
                  height: 40,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <FittingRoomIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" component="h2" fontWeight={700} noWrap>
              {room.number}
            </Typography>
            {isOccupied && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {room.shopperName || t("operations.debug.labels.anonymous")}
              </Typography>
            )}
          </Box>

          {isOccupied && (
            <Chip
              size="small"
              variant="outlined"
              aria-label={`${formatInteger(
                itemCount,
                i18n.resolvedLanguage || i18n.language,
              )} ${t("liveView.fittingRooms.shopperDetails.items.title")}, ${formatInteger(
                requestCount,
                i18n.resolvedLanguage || i18n.language,
              )} ${t("liveView.fittingRooms.shopperDetails.requests.title")}`}
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    <FittingRoomIcon sx={{ fontSize: 16 }} />
                    {formatInteger(itemCount, i18n.resolvedLanguage || i18n.language)}
                  </Box>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ width: "1px", height: 16, mx: 0.75, bgcolor: "grey.400" }}
                  />
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    <RequestsIcon sx={{ fontSize: 16 }} />
                    {formatInteger(requestCount, i18n.resolvedLanguage || i18n.language)}
                  </Box>
                </Box>
              }
              sx={{
                flexShrink: 0,
                bgcolor: "grey.100",
                borderColor: "grey.300",
                color: "common.black",
                "& .MuiChip-label": { display: "inline-flex", alignItems: "center" },
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
            />
          )}

          {isOccupied && timers[room.id] && (
            <Chip
              size="small"
              variant="outlined"
              icon={<ClockIcon />}
              label={timers[room.id]}
              color={isTimerOverdue ? "error" : "default"}
              sx={{
                flexShrink: 0,
                fontVariantNumeric: "tabular-nums",
                "& .MuiChip-icon": { color: "inherit" },
                ...(isTimerOverdue
                  ? {}
                  : {
                      bgcolor: "#dbeafe",
                      borderColor: "#93c5fd",
                      color: "#1e40af",
                    }),
              }}
            />
          )}

          {isOccupied && shopper && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={isVacating}
              onClick={() => {
                void handleVacateRoom(shopper.id);
              }}
              startIcon={<LogOutIcon fontSize="small" />}
            >
              {t("operations.debug.actions.vacate")}
            </Button>
          )}
        </Box>

        <Collapse in={isDetailsExpanded} timeout={200}>
          {hasRequests && (
            <Box sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <RequestsIcon sx={{ fontSize: 16, color: "common.black" }} />
                <Typography variant="subtitle2" sx={{ color: "common.black" }}>
                  {t("liveView.fittingRooms.shopperDetails.requests.title")}
                </Typography>
                <Chip
                  size="small"
                  label={formatInteger(
                    room.requests?.length ?? 0,
                    i18n.resolvedLanguage || i18n.language,
                  )}
                  sx={{
                    bgcolor: "grey.100",
                    borderColor: "grey.500",
                    color: "common.black",
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
                {room.requests?.map((request) => (
                  <RequestCard
                    key={`request-${request.id}-${request.type}`}
                    request={request}
                    selectedStore={state.selection.selectedStore}
                  />
                ))}
              </Box>
            </Box>
          )}
          {hasItems && (
            <Box sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <FittingRoomIcon sx={{ fontSize: 16, color: "common.black" }} />
                <Typography variant="subtitle2" sx={{ color: "common.black" }}>
                  {t("liveView.fittingRooms.shopperDetails.items.title")}
                </Typography>
                <Chip
                  size="small"
                  label={formatInteger(
                    room.items?.length ?? 0,
                    i18n.resolvedLanguage || i18n.language,
                  )}
                  sx={{
                    bgcolor: "grey.100",
                    borderColor: "grey.500",
                    color: "common.black",
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
                {sortedItems.map((item) => (
                  <ItemCard
                    key={`${item.epc}-${item.sku}`}
                    item={item}
                    selectedStore={state.selection.selectedStore}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Collapse>
      </Paper>
    );
  };

  return (
    <div>
      <ComparisonPageHeaderShell
        title={t("nav.fittingRoomsLiveView")}
        gradientTitle
        icon={<FittingRoomIcon />}
        loading={isPageLoading}
        headerControls={
          <InsightsHeaderSelectorRow>
            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 260, lg: 360 } }}>
              <StoreMultiSelect
                className="w-full font-bold"
                minDropdownWidth={440}
                dropdownAlign="right"
                options={filteredStores}
                value={state.selection.selectedStore ? [state.selection.selectedStore] : []}
                currentUserRole={user?.role}
                isDemo={(s) => {
                  const name = String((s as any).name || "").toLowerCase();
                  const group = String((s as any).group || "").toLowerCase();
                  return name.includes("demo") || group.includes("demo");
                }}
                onChange={(next) => {
                  const sel =
                    next && (next as any[]).length > 0 ? ((next as any[])[0] as Store) : null;
                  handleStoreSelect(sel);
                }}
                label={t("storeInsights.common.fields.stores")}
                disabled={selectionLoading}
                loading={selectionLoading}
                getOptionLabel={(s) => formatStoreSelectorLabel(s, t, user?.role, "id-name")}
                getOptionKey={(s) => (s as any).id as number}
                emptyText={t("storeInsights.common.empty.noStores")}
                single
              />
            </InsightsHeaderSelectorField>
          </InsightsHeaderSelectorRow>
        }
      />

      <div className="p-5 pt-0">
        {/* Main Content - Fitting Rooms Section */}
        <div className="rounded-lg">
          {state.error ? (
            <Box sx={{ minHeight: { xs: 240, sm: 300 }, display: "grid", placeItems: "center" }}>
              <ErrorState
                title={state.error}
                onRetry={() => {
                  setState((prev) => ({
                    ...prev,
                    error: null,
                    loading: {
                      ...prev.loading,
                      isLoadingData: true,
                    },
                  }));
                  if (state.selection.selectedStore?.id) {
                    void fetchChangingRoomData();
                  }
                }}
              />
            </Box>
          ) : isPageLoading ? (
            <LiveViewSkeleton canUseCompareMode={canUseCompareMode} isMobile={isMobile} />
          ) : state.data.areas.length === 0 ? (
            <Box sx={{ minHeight: { xs: 240, sm: 300 }, display: "grid", placeItems: "center" }}>
              <AdminEmptyState
                icon={<FittingRoomIcon sx={{ fontSize: 40 }} />}
                text={state.selection.selectedStore ? t("common.noData") : t("common.selectStore")}
              />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: { xs: 1, sm: 2 },
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Tabs
                  value={state.selection.activeTab}
                  onChange={(_, newValue) =>
                    setState((prev) => ({
                      ...prev,
                      selection: {
                        ...prev.selection,
                        activeTab: newValue,
                      },
                    }))
                  }
                  aria-label="fitting room areas"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {state.data.areas.map((area) => (
                    <Tab
                      key={`tab-${area.id}`}
                      label={
                        <Box sx={{ textAlign: "start" }} className="pt-2 pb-2">
                          <Typography
                            variant={isMobile ? "body2" : "subtitle2"}
                            component="div"
                            sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, fontWeight: 600 }}
                          >
                            {area.name}
                          </Typography>
                        </Box>
                      }
                      value={area.id}
                      sx={{ minHeight: AREA_TAB_HEIGHT }}
                    />
                  ))}
                </Tabs>
              </Box>

              {canUseCompareMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    px: { xs: 2, sm: 4 },
                    pt: isMobile ? 2 : 4,
                    pb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Switch
                      checked={isCompareMode}
                      onChange={(_, checked) => setIsCompareMode(checked)}
                      slotProps={{ input: { "aria-label": t("liveView.actions.compareMode") } }}
                      sx={{ height: COMPARE_MODE_CONTROL_HEIGHT }}
                    />
                    <Box
                      component="img"
                      src="/images/icons/crave_icon.svg"
                      alt=""
                      sx={{ width: 18, height: 18, mx: 0.5 }}
                    />
                    <Typography variant="body2">{t("liveView.actions.compareMode")}</Typography>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  px: isMobile ? 2 : 4,
                  pt: canUseCompareMode ? 0 : { xs: 2, sm: 4 },
                  pb: isMobile ? 2 : 4,
                }}
              >
                {state.data.areas.map((area) => (
                  <TabPanel
                    key={`area-${area.id}`}
                    value={state.selection.activeTab}
                    index={area.id}
                  >
                    <Box
                      sx={{
                        mt: 0,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                        alignItems: "start",
                        gap: 2,
                      }}
                    >
                      {isCompareMode ? (
                        <DragDropProvider onDragEnd={(event) => handleRoomReorder(area.id, event)}>
                          {area.rooms.map((room, index) => (
                            <SortableRoomCard
                              key={`room-${room.id}`}
                              areaId={area.id}
                              index={index}
                              room={room}
                            >
                              {(dragHandleRef) => renderRoomPaper(room, dragHandleRef)}
                            </SortableRoomCard>
                          ))}
                        </DragDropProvider>
                      ) : (
                        area.rooms.map((room) => renderRoomPaper(room))
                      )}
                    </Box>
                  </TabPanel>
                ))}
              </Box>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveView;
